<template>
  <v-card class="pa-5 mb-5 elevation-hb">
    <v-row align="center">
      <v-col >
        <TheTitle icon="mdi-cart-outline" text="Acompanhamento de Pedido" />
      </v-col>
      <v-col class="text-right" cols="2">
        <v-btn 
          depressed 
          color="primary"
          class="text-center mr-1" 
          :disabled="!orderReturn.length"
          :dark="orderReturn.length" 
          @click="downloadCSV()" 
          block
        >
          <v-icon small class="mx-2">mdi-magnify</v-icon>
          Exportar CSV
        </v-btn>
      </v-col>
    </v-row>
    <v-divider class="my-3"></v-divider>
    <v-row align="center">
      <v-col>
        <TheSearchInput v-model="search" />
      </v-col>
      <v-col>
        <FilterSelect v-model="filtrosSelecionados" :filters="filtros" />
      </v-col>
      <v-col v-if="sellers.length > 1" cols="2">
        <TheSellerSelect :sellers="sellers" v-model="sellerFilter" @change="toggleSellerFilter()" />
      </v-col>
      <v-col cols="2">
        <TheDatePicker v-model="date" @ok="loadOrdersByDate()" />
      </v-col>
      <v-col >
        <v-row>
          <v-col cols="4">
            <TheDatePeriod @click="loadOrdersByPeriod" />
          </v-col>
          <v-col cols="6" class="text-right ms-2">
            <FilterDelivery v-model="delivery" />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <TheDateAlert :message="infoMessage" :active="dataSelect" @click="loadOrders()" />
    <v-divider class="my-3"></v-divider>
    <v-data-table
      dense
      :headers="headersFiltered"
      :items="orderReturn"
      :items-per-page="30"
      :loading="loading"
      :search="search"
    >
      <template v-slot:[`item.orderIdMarketplace`]="{ item }">
        <OrderIdMarketplace :orderId="item.orderIdMarketplace" />
      </template>
      <template v-slot:[`item.clientFirstName`]="{ item }">
        {{ item.clientFirstName | capitalize }} {{ item.clientLastName | capitalize }}
      </template>
      <template v-slot:[`item.orderTracking`]="{ item }">
        <TrackingButton :tracking="item.orderTracking" :url="item.orderTrackingUrl" />
      </template>
      <template v-slot:[`item.destiny`]="{ item }">
        <span v-if="item.addressCity"> {{ item.addressCity }} - {{ item.addressState }}</span>
      </template>
      <template v-slot:[`item.orderAuthorizedDate`]="{ item }">
        <AuthorizationDate :date="item.orderAuthorizedDate" :sendDate="item.orderSendDate" />
      </template>
      <template v-slot:[`item.orderExpectedDateMarketplace`]="{ item }">
        <ExpectDate
          :date="item.orderAuthorizedDate"
          :offset="item.logisticShippingEstimate"
          :delivery="item.orderDeliveryDate"
        />
      </template>
      <template v-slot:[`item.orderSendDate`]="{ item }">
        <SendDate
          :date="item.orderSendDate"
          :authorizate="item.orderAuthorizedDate"
          :orderId="item.orderIdHubble"
          v-model="item.orderSendDate"
          v-if="cantEdit"
        />
        <span v-if="!cantEdit"> {{ item.orderSendDate }} </span>
      </template>
      <template v-slot:[`item.orderExpectedDate`]="{ item }">
        <ExpectedDateCarrier
          :date="item.orderExpectedDate"
          :sendDate="item.orderSendDate"
          :orderId="item.orderIdHubble"
          v-model="item.orderExpectedDate"
          v-if="cantEdit"
        />
        <span v-if="!cantEdit"> {{ item.orderExpectedDate }} </span>
      </template>
      <template v-slot:[`item.orderDeliveryDate`]="{ item }">
        <DeliveryDate
          :date="item.orderDeliveryDate"
          :sendDate="item.orderSendDate"
          :authorizate="item.orderAuthorizedDate"
          :offset="item.logisticShippingEstimate"
          :orderId="item.orderIdHubble"
          v-model="item.orderDeliveryDate"
          v-if="cantEdit"
        />
        <span v-if="!cantEdit"> {{ item.orderDeliveryDate }} </span>
      </template>
      <template v-slot:[`item.observacao`]="{ item }">
        <Messages :order="item" />
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import { addBusinessDays, formatStringData, checkDateLess, checkDateGreater } from '@/plugins/generic.js'
import { baseApiUrl, showError } from '@/global'
import { mask } from 'vue-the-mask'

import TheDateAlert from '@/components/TheDateAlert'
import TheDatePeriod from '@/components/TheDatePeriod'
import TheDatePicker from '@/components/TheDatePicker'
import TheTitle from '@/components/TheTitle'
import TheSearchInput from '@/components/TheSearchInput'
import TheSellerSelect from '@/components/TheSellerSelect'

import AuthorizationDate from '@/components/orderReport/AuthorizationDate'
import DeliveryDate from '@/components/orderReport/DeliveryDate'
import ExpectDate from '@/components/orderReport/ExpectDate'
import ExpectedDateCarrier from '@/components/orderReport/ExpectedDateCarrier'
import SendDate from '@/components/orderReport/SendDate'

import FilterDelivery from '@/components/orderReport/FilterDelivery'
import FilterSelect from '@/components/orderReport/FilterSelect'
import Messages from '@/components/orderReport/Messages'
import OrderIdMarketplace from '@/components/orderReport/OrderIdMarketplace'
import TrackingButton from '@/components/orderReport/TrackingButton'

export default {
  name: 'Report',
  directives: { mask },
  components: {
    AuthorizationDate,
    DeliveryDate,
    ExpectDate,
    ExpectedDateCarrier,
    FilterDelivery,
    FilterSelect,
    Messages,
    OrderIdMarketplace,
    TheDateAlert,
    TheDatePeriod,
    TheDatePicker,
    TheTitle,
    TheSearchInput,
    TheSellerSelect,
    TrackingButton,
    SendDate,
  },
  computed: {
    headersFiltered() {
      return this.filterSelect()
    },
    orderReturn() {
      return this.orderFilter()
    },
    ...mapState(['user']),
    cantEdit() {
      if (this.user && this.user.groups.includes('timecs')) {
        return false
      }
      return true
    },
  },
  data: () => ({
    infoMessage: null,
    date: [],
    filtrosSelecionados: [],
    filtros: [
      { text: 'Destino', value: 'destiny' },
      { text: 'Data Aprovação', value: 'orderAuthorizedDate', width: '135px' },
      { text: 'Prev. Entrega Loja', value: 'orderExpectedDateMarketplace' },
      {
        text: 'Data Postagem',
        value: 'orderSendDate',
        width: '135px',
      },
      {
        text: 'Prev. Transp',
        value: 'orderExpectedDate',
        width: '135px',
      },
      { text: 'Data Entrega', value: 'orderDeliveryDate', width: '130px' },
      { text: 'Rastreio', value: 'orderTracking' },
      { text: 'Aviso', value: 'observacao', width: '5%', align: 'center' },
    ],
    fixed: [
      { text: 'Pedido', value: 'orderIdMarketplace', width: '220px' },
      { text: 'Seller', value: 'sellerId' },
      { text: 'Cliente', value: 'clientFirstName', width: '200px' },
    ],
    delivery: false,
    headers: [],
    loading: false,
    dataSelect: false,
    orderActual: {},
    orders: [],
    period: null,
    statusFilter: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    search: null,
    size: 0,
    sellers: [],
    sellerFilter: [],
    order: null,
    orderSendDate: null,
    editIndex: null,
  }),
  methods: {
    addBusinessDays,
    checkDateLess,
    checkDateGreater,
    formatStringData,
    filterSelect() {
      localStorage.setItem('filtrosSelecionados-report', JSON.stringify(this.filtrosSelecionados))
      let arr = []
      if (this.filtros.length > 0) {
        arr = this.filtros.filter((element) => {
          if (this.filtrosSelecionados.find((el) => el == element.value)) {
            return element
          }
        })
      }
      return this.fixed.concat(arr)
    },
    loadOrders() {
      this.loading = true
      const url = `${baseApiUrl}/order/report`
      this.orders = []
      axios
        .get(url)
        .then((res) => {
          this.orders = res.data
          this.loading = false
          this.dataSelect = false

          localStorage.setItem('period-report', JSON.stringify(null))
          localStorage.setItem('date-report', JSON.stringify([]))
          this.date = []
        })
        .catch(showError)
    },
    loadOrdersByDate() {
      if (this.date.length > 0) {
        this.loading = true
        this.dataSelect = false
        this.order = {}
        this.orders = []
        const url = `${baseApiUrl}/order/report/dateRange`
        axios
          .post(url, { date: this.date })
          .then(async (res) => {
            if (this.date.length === 1) {
              this.infoMessage = `Filtrando por data: ${this.$options.filters.formatDate(this.date[0])}.`
            } else if (this.date.length > 1) {
              this.infoMessage = `Filtrando por data: ${this.$options.filters.formatDate(
                this.date[0]
              )} à ${this.$options.filters.formatDate(this.date[1])}.`
            }
            this.orders = res.data
            this.loading = false
            this.dataSelect = true
            localStorage.setItem('period-report', JSON.stringify(null))
            localStorage.setItem('date-report', JSON.stringify(this.date))
          })
          .catch(showError)
      }
    },
    loadOrdersByPeriod(period) {
      this.period = period
      this.loading = true
      this.dataSelect = false
      this.order = {}
      this.orders = []
      const url = `${baseApiUrl}/order/report/period`
      axios
        .post(url, { period })
        .then(async (res) => {
          this.infoMessage = `Filtrando por Período: Últimos ${period} dias.`
          this.orders = res.data
          this.loading = false
          this.dataSelect = true
          localStorage.setItem('date-report', JSON.stringify([]))
          localStorage.setItem('period-report', JSON.stringify(period))
        })
        .catch(showError)
    },
    loadSellers() {
      const url = `${baseApiUrl}/seller`
      axios
        .get(url)
        .then((res) => {
          this.sellers = res.data
        })
        .catch(showError)
        .finally((this.loading = false))
    },
    toggleSellerFilter() {
      localStorage.setItem('sellerFilter-report', JSON.stringify(this.sellerFilter))
    },
    orderFilter() {
      let orderTemp = []
      let orderReturn = []
      this.orders.map((element) => {
        this.statusFilter.forEach((fil) => {
          if (fil == element.orderSellerStatus) {
            orderTemp.push(element)
          }
        })
      })
      if (this.sellerFilter.length > 0) {
        orderTemp.map((element) => {
          this.sellerFilter.forEach((fil) => {
            if (fil == element.sellerId) {
              orderReturn.push(element)
            }
          })
        })
      } else {
        orderReturn = orderTemp
      }
      orderTemp = []
      if (this.delivery) {
        orderReturn.map((element) => {
          // if (!element.orderDeliveryDate || element.orderDeliveryDate.length < 11) {
          if (!element.orderDeliveryDate) {
            orderTemp.push(element)
          }
        })
      } else {
        orderTemp = orderReturn
      }
      return orderTemp
    },
    downloadCSV() {
      const data = this.orderReturn
      const fileName = 'Acompanhamento-de-pedido'
      const csv = this.convertArrayOfObjectsToCSV(data)
      const link = document.createElement('a')
      link.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
      link.download = fileName + '.csv'
      link.click()
    },
    convertArrayOfObjectsToCSV(data) {
      const columnDelimiter = ','
      const lineDelimiter = '\n'
      const keys = Object.keys(data[0])

      let result = ''
      result += keys.join(columnDelimiter)
      result += lineDelimiter

      data.forEach((item) => {
        let ctr = 0
        keys.forEach((key) => {
          if (ctr > 0) result += columnDelimiter
          result += item[key]
          ctr++
        })
        result += lineDelimiter
      })

      return result
    },
  },
  created() {
    if (localStorage.getItem('date-report')) {
      this.date = JSON.parse(localStorage.getItem('date-report'))
    }
    if (localStorage.getItem('filtrosSelecionados-report')) {
      this.filtrosSelecionados = JSON.parse(localStorage.getItem('filtrosSelecionados-report'))
    }
    if (localStorage.getItem('period-report')) {
      this.period = JSON.parse(localStorage.getItem('period-report'))
    }
    if (localStorage.getItem('sellerFilter-report')) {
      this.sellerFilter = JSON.parse(localStorage.getItem('sellerFilter-report'))
    }

    if (this.date.length > 0) {
      this.loadOrdersByDate()
    } else if (this.period != null) {
      this.loadOrdersByPeriod(this.period)
    } else {
      this.loadOrdersByPeriod(15)
    }
    this.loadSellers()
  },
}
</script>
